/**
 * 紹介状況（クリック・会員登録・紹介成立・獲得した無料日数）。
 *
 * 数値はすべて API の返り値をそのまま出す。累積の計算はサーバー側が正本で、
 * ここでは「件数と日数が噛み合わないときは内訳を出さない」判断だけを持つ。
 * click_count / signup_count は BQ 集計側の都合で欠けることがあるため任意扱い。
 */
import { computed, ref } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { fetchReferralStatus } from '@/services/yagiprime/referral-campaign-client'
import type { ReferralStatusResponse } from '@/services/yagiprime/referral-campaign-client'
import { formatGrantedDaysBreakdown } from '@/utils/yagiprime/referralCampaign'
import campaign from '@/utils/yagiprime/referralCampaign.json'

const status = ref<ReferralStatusResponse | null>(null)
const loading = ref(false)
let fetchedUid = ''

export function useReferralStatus() {
  const { whenAuthReady } = useAuth()

  async function loadStatus(): Promise<void> {
    const user = await whenAuthReady()
    const uid = user?.uid
    if (!uid) {
      fetchedUid = ''
      status.value = null
      return
    }
    if (fetchedUid === uid) return
    fetchedUid = uid
    loading.value = true
    const data = await fetchReferralStatus(uid)
    loading.value = false
    if (fetchedUid !== uid) return
    if (!data) {
      // 取得に失敗したら「判定済み」にせず、次の呼び出しで取り直す。
      // ここを握ると一度の 500 で紹介導線も「◯日間無料」も出なくなる
      fetchedUid = ''
      status.value = null
      return
    }
    status.value = data
  }

  /**
   * 取得済みでも取り直す。件数・獲得日数・`pending_days_total` は紹介が成立した
   * 時点でサーバー側が増えるため、SPA を開いたままだと `loadStatus` の
   * キャッシュで古い値が残る。**加入モーダルを開くときは必ずこちらを使う**
   * （「◯日間無料」は課金条件の表示なので、古い値を出してはいけない）。
   */
  async function refreshStatus(): Promise<void> {
    fetchedUid = ''
    await loadStatus()
  }

  const successCount = computed(() => status.value?.referral_success_count ?? 0)
  const grantedDaysTotal = computed(() => status.value?.granted_days_total ?? 0)

  /** 集計が返らなかったぶんは表示自体を落とす（0 と欠損を混ぜない） */
  const clickCount = computed(() => status.value?.click_count ?? null)
  const signupCount = computed(() => status.value?.signup_count ?? null)
  const hasFunnelCounts = computed(
    () => clickCount.value !== null || signupCount.value !== null
  )

  /** 「30日 × 2件 ＝ 60日」。噛み合わないときは空文字 */
  const grantedBreakdown = computed(() =>
    formatGrantedDaysBreakdown(
      campaign.offer.freeDaysPerReferral,
      successCount.value,
      grantedDaysTotal.value
    )
  )

  /** 成立済みで獲得した無料日数があるか */
  const hasGrantedDays = computed(() => grantedDaysTotal.value > 0)

  /**
   * 誰かの紹介で登録した会員か。被紹介者は紹介する側になれないので、
   * 紹介URL・シェア導線はこれが true のときは出さない（サーバー側も
   * コード発行と紐付けを拒否する）。
   */
  const isReferee = computed(() => status.value?.is_referee === true)

  /**
   * 紹介する側になれるか（サーバーの is_eligible_as_referrer）。
   * シェア導線の出し分けはこれを使う。**「未加入か」で判定しない**:
   * キャンペーン期間中に加入した会員も紹介者のままで、成立するたびに
   * サーバーが trial_end を伸ばす。加入を条件に導線を消すと、その
   * 「加入してからも紹介して無料期間を伸ばす」経路に到達できなくなる。
   */
  const canRefer = computed(() => status.value?.can_refer === true)

  /** 紹介成立が上限（12件・合計360日）に達したか */
  const isReferralCapReached = computed(
    () => status.value?.referral_cap_reached === true
  )

  /**
   * 判定が返っているか。返るまでシェア導線を出さないことで、被紹介者に
   * 一瞬だけ紹介URLが見える（＝誤訴求）のを防ぐ。取得に失敗したときも
   * false のままで、その場合は導線を出さない側に倒す。
   */
  const isStatusChecked = computed(() => status.value !== null)

  /**
   * 次の加入で trial_end に乗る合計日数（被紹介者分＋紹介者として溜まった分）。
   * 加入画面の「◯日間無料」「◯月◯日まで無料」はこれを使う。
   */
  const pendingDaysTotal = computed(() => status.value?.pending_days_total ?? 0)

  return {
    status,
    loading,
    loadStatus,
    refreshStatus,
    successCount,
    grantedDaysTotal,
    clickCount,
    signupCount,
    hasFunnelCounts,
    grantedBreakdown,
    hasGrantedDays,
    isReferee,
    canRefer,
    isReferralCapReached,
    isStatusChecked,
    pendingDaysTotal
  }
}
